'use client';

import { useQuery } from '@apollo/client';
import { useSession } from '@ory/elements-react/client';
import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';

import { AuthProvider_GetCurrentUserDocument } from '@/graphql';

// Auth context type
interface AuthContextType {
  isLoading: boolean;
  isLoggedIn: boolean;
  currentUserId: string | undefined;
  currentPersonId: string | undefined;
  currentEntityId: string | undefined;
  currentEntityProfileImageUrl: string | undefined;
  isCurrentUserVerified: boolean;
  isCurrentUserALeader: boolean;
  setCurrentUserId: (userId: string) => void;
  setCurrentPersonId: (personId: string) => void;
  setCurrentEntityId: (entityId: string) => void;
  setCurrentEntityProfileImageUrl: (profileImageUrl: string) => void;
  setIsCurrentUserVerified: (isVerified: boolean) => void;
  setIsCurrentUserALeader: (isLeader: boolean) => void;
  resetState: () => void;
  refetchUser: () => void;
}

const defaultContext: AuthContextType = {
  isLoading: true,
  isLoggedIn: false,
  currentUserId: undefined,
  currentPersonId: undefined,
  currentEntityId: undefined,
  currentEntityProfileImageUrl: undefined,
  isCurrentUserVerified: false,
  isCurrentUserALeader: false,
  setCurrentUserId: () => {},
  setCurrentPersonId: () => {},
  setCurrentEntityId: () => {},
  setCurrentEntityProfileImageUrl: () => {},
  setIsCurrentUserVerified: () => {},
  setIsCurrentUserALeader: () => {},
  resetState: () => {},
  refetchUser: () => {},
};

// Default context
const AuthContext = createContext<AuthContextType>(defaultContext);

// Hook for using auth context
const useAuth = () => useContext(AuthContext);

// Provider component
const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [isLoading, setIsLoading] = useState<boolean>(
    defaultContext?.isLoading,
  );
  const [isLoggedIn, setIsLoggedIn] = useState<boolean>(
    defaultContext?.isLoading,
  );
  const [currentUserId, setCurrentUserId] = useState<string | undefined>(
    defaultContext?.currentUserId,
  );
  const [currentPersonId, setCurrentPersonId] = useState<string | undefined>(
    defaultContext?.currentPersonId,
  );
  const [currentEntityId, setCurrentEntityId] = useState<string | undefined>(
    defaultContext?.currentEntityId,
  );
  const [currentEntityProfileImageUrl, setCurrentEntityProfileImageUrl] =
    useState<string | undefined>(defaultContext?.currentEntityProfileImageUrl);

  const [isCurrentUserVerified, setIsCurrentUserVerified] = useState<boolean>(
    defaultContext?.isCurrentUserVerified,
  );

  const [isCurrentUserALeader, setIsCurrentUserALeader] = useState<boolean>(
    defaultContext?.isCurrentUserALeader,
  );

  const resetState = useCallback(() => {
    localStorage.removeItem('auth-context');

    setIsLoading(true);
    setIsLoggedIn(false);
    setCurrentUserId(undefined);
    setCurrentPersonId(undefined);
    setCurrentEntityId(undefined);
    setCurrentEntityProfileImageUrl(undefined);
    setIsCurrentUserVerified(false);
    setIsCurrentUserALeader(false);
    setIsLoading(false);
  }, []);

  const isBrowser = typeof window !== 'undefined';

  // Use the Ory session hook
  const { session, isLoading: sessionIsLoading } = useSession();

  const oryId = session?.identity?.id;

  useEffect(() => {
    setIsLoggedIn(session?.active ?? false);
  }, [session?.active]);

  const {
    data: {
      users: [
        {
          id: userId,
          person: {
            id: personId,
            voterVerifications: voterVerifications = [],
            entityPersonRels: [
              {
                entity: {
                  id: entityId,
                  profilePhotoId: entityProfilePhotoId,
                  profilePhoto,
                  isLeader,
                } = {},
              } = {},
            ] = [],
          } = {},
        } = {},
      ] = [],
    } = {},
    loading: userIsLoading,
    refetch: refetchUser,
  } = useQuery(AuthProvider_GetCurrentUserDocument, {
    variables: { oryId },
    skip: !oryId,
    fetchPolicy: 'network-only',
    nextFetchPolicy: 'cache-and-network',
  });

  useEffect(() => {
    setCurrentUserId(userId);
  }, [userId]);

  useEffect(() => {
    setCurrentPersonId(personId);
  }, [personId]);

  useEffect(() => {
    setCurrentEntityId(entityId);
  }, [entityId]);

  useEffect(() => {
    setIsLoading(sessionIsLoading || userIsLoading);
  }, [sessionIsLoading, userIsLoading]);

  useEffect(() => {
    setIsCurrentUserVerified(
      // TODO check if the verification is recent enough
      Array.isArray(voterVerifications) && voterVerifications.length > 0,
    );
  }, [voterVerifications]);

  useEffect(() => {
    setCurrentEntityProfileImageUrl(profilePhoto?.link);
  }, [profilePhoto?.link, entityProfilePhotoId]);

  useEffect(() => {
    setIsCurrentUserALeader(isLeader ?? false);
  }, [isLeader]);

  // Restore context from localStorage
  useEffect(() => {
    // Only restore from localStorage if we have an active session
    if (isBrowser) {
      if (
        session?.active &&
        !currentUserId &&
        !currentEntityId &&
        !currentPersonId
      ) {
        const savedContext = localStorage.getItem('auth-context') ?? '{}';
        const {
          currentUserId: savedUserId,
          currentEntityId: savedEntityId,
          currentPersonId: savedPersonId,
          currentEntityProfileImageUrl: savedEntityProfileImageUrl,
        } = JSON.parse(savedContext);

        if (savedUserId && savedEntityId) {
          setCurrentUserId(savedUserId);
          setCurrentEntityId(savedEntityId);
          setCurrentPersonId(savedPersonId);
          setCurrentEntityProfileImageUrl(savedEntityProfileImageUrl);
        }
      }
    }
  }, [isBrowser, session, currentUserId, currentEntityId, currentPersonId]);

  // Save to localStorage only when we have verified user data and an active session
  useEffect(() => {
    if (
      isBrowser &&
      !isLoading &&
      currentUserId &&
      currentEntityId &&
      currentPersonId &&
      session?.active
    ) {
      localStorage.setItem(
        'auth-context',
        JSON.stringify({
          currentUserId,
          currentEntityId,
          currentPersonId,
          currentEntityProfileImageUrl,
        }),
      );
    }
  }, [
    isBrowser,
    isLoading,
    session,
    currentUserId,
    currentEntityId,
    currentPersonId,
    currentEntityProfileImageUrl,
  ]);

  return (
    <AuthContext.Provider
      value={{
        isLoading,
        isLoggedIn,
        currentUserId,
        currentEntityId,
        currentPersonId,
        isCurrentUserVerified,
        isCurrentUserALeader,
        currentEntityProfileImageUrl,
        setCurrentPersonId,
        setCurrentUserId,
        setCurrentEntityId,
        setCurrentEntityProfileImageUrl,
        setIsCurrentUserVerified,
        setIsCurrentUserALeader,
        resetState,
        refetchUser,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export { AuthProvider, useAuth };
